import type { DYNAMO_TABLENAMES } from '../databaseUtils';

import type { CreateClientAccountInput, InvoiceDTO, PostingDTO } from './dto-app';
import type { Invoice, Posting } from './model-app';

export type ClientSignupDetails = Pick<
  CreateClientAccountInput,
  'address' | 'businessNumber' | 'ipiNumber' | 'judicialDistrict' | 'language' | 'vat'
>;

export interface LooseObject {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
}

export interface LooseObjectWithId {
  id: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
}

export type LooseQueryGet<T> = {
  [key: string]: T | null;
};

export type LooseQueryGetInput = {
  id: unknown | null;
};

export type LooseQueryList<T> = {
  [key: string]: {
    __typename: string;
    items: Array<T | null> | null;
    nextToken: string | null;
    startedAt: number | null;
  } | null;
};

export type LooseQueryListInput = {
  filter?: object | null;
  limit?: number | null;
  nextToken?: string | null;
  lastSync?: number | null;
  key?: string | null;
};

export type BasicModel<T> = T & { id: string; _version: number; clientId: string; readId: string };

export type ModelWithVersion<T> = T & { _version: number };

export type Model = (typeof DYNAMO_TABLENAMES)[number];

export type InvoiceWithPostings = Invoice & { postings: Posting[] };

export type InvoiceWithPostingsDTO = InvoiceDTO & { postings: PostingDTO[] };

export type PostingWithReconciledPosting = Posting & { linkedPosting: Posting };

type ModelDTO<T> = T & { __typename: string; _lastChangedAt: number };

type ModelEntity<T> = {
  [K in keyof T]: T[K] extends object ? (T[K] extends Array<infer U> ? ModelEntity<U>[] : ModelEntity<T[K]>) : T[K];
};

export const convertDtoToEntity = <T>(dto: ModelDTO<T>): ModelEntity<T> => {
  if (typeof dto !== 'object' || dto === null) return dto as ModelEntity<T>;
  if (Array.isArray(dto)) return dto.map((item) => convertDtoToEntity(item)) as ModelEntity<T>;

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { __typename, _lastChangedAt, ...rest } = dto;

  return Object.fromEntries(
    Object.entries(rest).map(([key, value]) => [key, convertDtoToEntity(value as ModelDTO<T>)])
  ) as ModelEntity<T>;
};

export type RelationsOf<T extends object, D extends number = 3> = [D] extends [0]
  ? never
  : T extends object[]
    ? RelationsOf<T[number], D>
    : keyof T extends infer K
      ? K extends string & keyof T
        ? NonNullable<T[K]> extends object
          ? K | `${K}.${RelationsOf<NonNullable<T[K]>, Prev[D]>}`
          : K
        : never
      : never;

type Prev = [never, 0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15];

export const filterAndMap =
  <T, U>(options: { filter: (value: T) => boolean; map: (value: T) => U }): ((acc: U[], current: T) => U[]) =>
  (acc, current) => {
    if (options.filter(current)) {
      acc.push(options.map(current));
    }
    return acc;
  };
